import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Sparkles, Heart, MapPin, Phone, Clock, ArrowRight } from "lucide-react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFAB from "@/components/shared/WhatsAppFAB";
import SEO from "@/components/shared/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, unwrap } from "@/lib/api";

const LAURA_IMG = "/static/team/laura-mora.jpg";
const TEAM_IMG = "https://images.pexels.com/photos/4239130/pexels-photo-4239130.jpeg";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

// Safe default copy — honest, no fabricated milestones. The cleaner of "facts that
// only Ramon can confirm" is now editable from /admin/settings → "About page copy".
const DEFAULT_COPY = `Ever Clean Home is a family-run residential cleaning service founded by Laura Mora and serving Boston, Cambridge, Brookline, Somerville, Providence and surrounding areas.

We focus on what every home owner actually wants: a clean that gets the dust behind the fridge and the grime behind the toilet, with the same team every time, and pricing you can trust before booking.

Want to share your story? Sign in to the admin panel and update the "About page copy" field — your text will replace this default copy automatically.`;

export default function AboutPage() {
  const [info, setInfo] = useState({ contact_name: "Laura Mora", company_phone: "", support_phone: "", hours: "", days_open: "", about_copy: "" });

  useEffect(() => {
    api.get("/public/settings").then((r) => {
      const d = unwrap(r) || {};
      setInfo((prev) => ({
        contact_name: d.contact_name || prev.contact_name,
        company_phone: d.company_phone || "",
        support_phone: d.support_phone || "",
        hours: d.hours || "",
        days_open: d.days_open || "",
        about_copy: d.about_copy || "",
      }));
    }).catch(() => {});
  }, []);

  const copyParagraphs = (info.about_copy || DEFAULT_COPY).split(/\n\n+/).filter(Boolean);

  const values = [
    { icon: Shield, t: "Insured & trusted", d: "Fully insured, LLC-registered in Massachusetts. Background-checked team." },
    { icon: Sparkles, t: "Detail-obsessed", d: "We don't \"surface clean\". We get the dust behind the fridge and the grime behind the toilet." },
    { icon: Heart, t: "Family-run", d: "Every cleaner we send is on our own payroll — vetted, trained, never subcontracted." },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="about-page">
      <SEO
        title="About us — Ever Clean Home"
        description="Ever Clean Home is a family-run residential cleaning service in Boston, MA. Insured, trained team, honest pricing, no phone calls."
      />
      <Header />
      <WhatsAppFAB />

      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FACC15]/20 text-[#0C1A1A] text-xs font-semibold mb-6">
              <Heart className="w-3.5 h-3.5" /> Our story
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
              Built by people <br />who <span className="text-[#0EA5A4]">actually clean.</span>
            </h1>
            <p className="mt-6 text-lg text-[#4B5563] max-w-xl leading-relaxed">
              Ever Clean Home is a family business making it ridiculously easy to book a real, detail-obsessed cleaning team — without phone calls or back-and-forth.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative">
            <div className="absolute -inset-5 bg-[#FACC15]/30 rounded-[2.5rem] -z-10 -rotate-2" />
            <img src={LAURA_IMG} alt={`${info.contact_name} — Founder`} className="rounded-3xl shadow-2xl w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-5 left-4 bg-white rounded-2xl shadow-xl p-3 px-4 border border-[#E5E7EB]">
              <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold">Founder</div>
              <div className="font-display font-extrabold" data-testid="about-founder-name">{info.contact_name}</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story (editable copy from admin) */}
      <section className="py-16 bg-white" data-testid="about-story-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8 space-y-5 text-[#0C1A1A]">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">A real cleaning team. Booked online.</h2>
          {copyParagraphs.map((p, i) => (
            <p key={i} className="text-[#4B5563] leading-relaxed text-base" data-testid={`about-paragraph-${i}`}>{p}</p>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">What we stand for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
            {values.map((v, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Card className="rounded-2xl p-6 border border-[#E5E7EB] h-full">
                  <span className="w-11 h-11 rounded-xl bg-[#0EA5A4]/10 text-[#0F766E] grid place-items-center mb-3"><v.icon className="w-5 h-5" /></span>
                  <div className="font-display font-bold text-lg">{v.t}</div>
                  <p className="text-sm text-[#4B5563] mt-1 leading-relaxed">{v.d}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team image + contact strip */}
      <section className="py-16 bg-[#0C1A1A] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <img src={TEAM_IMG} alt="Cleaning team" className="rounded-3xl aspect-[4/3] object-cover" />
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">Talk to a real human.</h2>
            <p className="mt-3 text-white/70">Got a question or a tricky cleaning job? Reach out — we answer fast.</p>
            <ul className="mt-6 space-y-3 text-white/80">
              {(info.company_phone || info.support_phone) && (
                <li className="flex items-center gap-3" data-testid="about-phone"><Phone className="w-4 h-4 text-[#FACC15]" /> {info.company_phone || info.support_phone}</li>
              )}
              {(info.days_open || info.hours) && (
                <li className="flex items-center gap-3" data-testid="about-hours">
                  <Clock className="w-4 h-4 text-[#FACC15]" />
                  <span>{[info.days_open, info.hours].filter(Boolean).join(" · ")}</span>
                </li>
              )}
              <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-[#FACC15]" /> Boston · Cambridge · Providence · Hartford</li>
            </ul>
            <div className="mt-7 flex gap-3 flex-wrap">
              <Link to="/book"><Button className="bg-[#FACC15] text-[#0C1A1A] hover:bg-[#FACC15]/90 rounded-xl font-semibold" data-testid="about-book-cta">Book a cleaning <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
              <Link to="/contact"><Button variant="outline" className="rounded-xl bg-transparent border-white/30 text-white hover:bg-white/10">Send a message</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
